import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { DatePicker } from '@amore/ui/components/date-picker'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { InputAddon, InputGroup } from '@amore/ui/components/input-group'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Link } from '@tanstack/react-router'
import { Check, ListChecks, Plus } from 'lucide-react'
import type { ReactNode } from 'react'
import { formatDate, formatMoney, isOverdue, newId, relativeDue } from '@/lib/format'
import { paymentMethodLabels } from '@/lib/labels'
import { replaceById } from '@/mock/store'
import type { Payment, PaymentMethod, Task } from '@/mock/types'
import { ItemAddRow, ItemCard, ItemCardFields } from '@/patterns/item-card'
import { datePickerLabels } from '@/screens/tasks/task-fields'

/** The names planners use over and over; one click fills them in. */
const suggestedNames = [
  'Záloha',
  'První platba',
  'Druhá platba',
  'Doplatek',
  'Platba na místě',
  'Celá částka',
]
const methods = Object.keys(paymentMethodLabels) as PaymentMethod[]
const dueLabels = {
  ...datePickerLabels,
  placeholder: 'Bez splatnosti',
  title: 'Splatnost platby',
  clear: 'Bez splatnosti',
}

const formatDay = (date: Date) =>
  date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })
const formatMonth = (date: Date) =>
  date.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })

/**
 * "Platby": how the item's price is paid. A line on top says what's left to
 * plan; each payment is an item card with name, amount, due date and method,
 * and its paid state as a labelled pill. A payment with a due date gets a task
 * in Úkoly when the sheet is saved.
 */
export function PaymentsTab({
  amount,
  payments,
  tasks,
  canEdit,
  onChange,
  onOpenItem,
}: {
  /** The item's price from the Položka tab, 0 when unknown. */
  amount: number
  payments: Payment[]
  tasks: Task[]
  canEdit: boolean
  onChange: (payments: Payment[]) => void
  onOpenItem: () => void
}) {
  const planned = payments.reduce((sum, payment) => sum + payment.amount, 0)
  const paid = payments.reduce((sum, payment) => sum + (payment.paid ? payment.amount : 0), 0)
  const left = amount - planned
  const patch = (id: string, value: Partial<Payment>) => onChange(replaceById(payments, id, value))
  const add = (name = '', value = Math.max(left, 0)) =>
    onChange([
      ...payments,
      { id: newId('pay'), name, amount: value, dueDate: null, paid: false, method: 'transfer' },
    ])

  return (
    <div className="space-y-5">
      <PlanSummary amount={amount} planned={planned} paid={paid} />

      {amount === 0 ? (
        <p className="text-sm text-muted-foreground">
          Položka zatím nemá cenu.{' '}
          <button type="button" onClick={onOpenItem} className="font-semibold text-action">
            Doplňte ji
          </button>
          , ať víte, kolik zbývá rozplánovat.
        </p>
      ) : left > 0 && payments.length > 0 ? (
        <Notice tone="warning">
          <span>
            Zbývá rozplánovat <strong className="tabular-nums">{formatMoney(left)}</strong>.
          </span>
          {canEdit ? (
            <Button
              appearance="outline"
              tone="neutral"
              size="sm"
              className="ml-auto"
              onClick={() => add('Doplatek', left)}
            >
              <Plus aria-hidden="true" className="size-3.5" />
              Přidat doplatek
            </Button>
          ) : null}
        </Notice>
      ) : left < 0 ? (
        <Notice tone="danger">
          <span>
            Platby jsou o <strong className="tabular-nums">{formatMoney(-left)}</strong> vyšší než
            cena položky.
          </span>
        </Notice>
      ) : null}

      {payments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? 'Zatím žádná platba. Rozepište zálohu a doplatek, ať vám nic neuteče.'
            : 'Zatím žádná platba.'}
        </p>
      ) : null}

      {payments.map((payment, index) => {
        const task = tasks.find((item) => item.payment?.paymentId === payment.id)
        const late = !payment.paid && isOverdue(payment.dueDate)
        return (
          <ItemCard
            key={payment.id}
            index={index + 1}
            title={payment.name}
            fallbackTitle="Nová platba"
            meta={
              <span className={late ? 'text-danger-foreground' : undefined}>
                {[
                  payment.amount ? formatMoney(payment.amount) : null,
                  payment.dueDate
                    ? late
                      ? relativeDue(payment.dueDate)
                      : `do ${formatDate(payment.dueDate)}`
                    : 'bez splatnosti',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            }
            menu={
              canEdit
                ? [
                    {
                      label: 'Odebrat platbu',
                      tone: 'danger' as const,
                      onSelect: () => onChange(payments.filter((item) => item.id !== payment.id)),
                    },
                  ]
                : []
            }
          >
            <fieldset disabled={!canEdit} className="space-y-4">
              <ItemCardFields>
                <Field label="Název">
                  <Input
                    value={payment.name}
                    placeholder="Např. Rezervační záloha"
                    onChange={(event) => patch(payment.id, { name: event.target.value })}
                  />
                  {canEdit ? (
                    <NamePresets
                      current={payment.name}
                      onPick={(name) => patch(payment.id, { name })}
                    />
                  ) : null}
                </Field>
                <Field label="Částka">
                  <InputGroup>
                    <Input
                      value={payment.amount ? payment.amount.toLocaleString('cs-CZ') : ''}
                      inputMode="numeric"
                      className="tabular-nums"
                      onChange={(event) =>
                        patch(payment.id, {
                          amount: Number(event.target.value.replace(/\D/g, '')) || 0,
                        })
                      }
                    />
                    <InputAddon side="end">Kč</InputAddon>
                  </InputGroup>
                </Field>
                <Field label="Splatnost">
                  <DatePicker
                    value={payment.dueDate ?? ''}
                    onValueChange={(value) => patch(payment.id, { dueDate: value || null })}
                    readOnly={!canEdit}
                    labels={dueLabels}
                    formatDate={formatDay}
                    formatMonth={formatMonth}
                  />
                </Field>
                <Field label="Způsob platby">
                  <Select
                    value={payment.method}
                    onValueChange={(method) =>
                      patch(payment.id, { method: method as PaymentMethod })
                    }
                  >
                    {methods.map((method) => (
                      <SelectItem key={method} value={method}>
                        {paymentMethodLabels[method]}
                      </SelectItem>
                    ))}
                  </Select>
                </Field>
              </ItemCardFields>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <PaidButton
                  paid={payment.paid}
                  canEdit={canEdit}
                  onToggle={() => patch(payment.id, { paid: !payment.paid })}
                />
                {task ? (
                  <Link
                    to={`/tasks/${task.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <ListChecks aria-hidden="true" className="size-3.5" />V úkolech ↗
                  </Link>
                ) : payment.dueDate && !payment.paid ? (
                  <span className="text-xs text-muted-foreground">
                    Po uložení přidáme úkol do Úkolů.
                  </span>
                ) : null}
              </div>
            </fieldset>
          </ItemCard>
        )
      })}

      {canEdit ? (
        <ItemAddRow>
          <Button
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={() => add(payments.length === 0 ? 'Záloha' : '')}
          >
            <Plus aria-hidden="true" className="size-3.5" />
            {payments.length === 0 ? 'Přidat zálohu' : 'Přidat platbu'}
          </Button>
          {payments.length === 0 && amount > 0 ? (
            <Button
              appearance="outline"
              tone="neutral"
              size="sm"
              onClick={() => add('Celá částka')}
            >
              Zaplatit celé najednou
            </Button>
          ) : null}
        </ItemAddRow>
      ) : null}
    </div>
  )
}

/** The usual payment names as quiet links under the name field; the current one is left out. */
function NamePresets({ current, onPick }: { current: string; onPick: (name: string) => void }) {
  return (
    <fieldset
      aria-label="Doporučené názvy plateb"
      className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5 px-1 text-xs"
    >
      {suggestedNames
        .filter((name) => name !== current.trim())
        .map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => onPick(name)}
            className="rounded-sm text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {name}
          </button>
        ))}
    </fieldset>
  )
}

/** Price, planned and paid side by side, so the gap is visible at a glance. */
function PlanSummary({ amount, planned, paid }: { amount: number; planned: number; paid: number }) {
  const items = [
    { label: 'Cena', value: amount ? formatMoney(amount) : 'Bez ceny' },
    { label: 'Rozplánováno', value: formatMoney(planned) },
    { label: 'Zaplaceno', value: formatMoney(paid), tone: paid > 0 ? 'success' : undefined },
  ]
  return (
    <dl className="grid grid-cols-3 gap-3">
      {items.map((item) => (
        <div key={item.label} className="space-y-0.5">
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd
            className={cn(
              'text-lg font-semibold tabular-nums',
              item.tone === 'success' && 'text-success-foreground',
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Notice({ tone, children }: { tone: 'warning' | 'danger'; children: ReactNode }) {
  return (
    <p
      className={cn(
        'flex flex-wrap items-center gap-x-1 gap-y-2 rounded-xl px-4 py-2.5 text-sm',
        tone === 'warning'
          ? 'bg-warning/60 text-warning-foreground'
          : 'bg-danger text-danger-foreground',
      )}
    >
      {children}
    </p>
  )
}

/** The payment's paid state as a labelled pill: one click marks it paid, another brings it back. */
function PaidButton({
  paid,
  canEdit,
  onToggle,
}: {
  paid: boolean
  canEdit: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!canEdit}
      aria-pressed={paid}
      className={cn(
        'group inline-flex items-center gap-2 rounded-full border py-1 pr-3.5 pl-1 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60',
        paid
          ? 'border-transparent bg-success text-success-foreground'
          : 'border-success-foreground/40 text-foreground hover:bg-success',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid size-6 place-items-center rounded-full border-2 transition-colors',
          paid
            ? 'border-success-foreground bg-success-foreground text-background'
            : 'border-success-foreground/60 text-success-foreground group-hover:bg-success-foreground group-hover:text-background',
        )}
      >
        <Check className="size-3" />
      </span>
      {paid ? (
        <>
          Zaplaceno
          <span className="font-normal text-muted-foreground group-hover:underline">Vrátit</span>
        </>
      ) : (
        'Označit jako zaplacené'
      )}
    </button>
  )
}
